import { MouseEventHandler, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";

import { uploadFile } from "~/api/media/upload";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslations from "~/localization/fr";
import {
  CreateMemberInput,
  SubscriptionFrequency,
} from "~/types/user/user.types";

import ApplicationInput from "../../input";

const ApplicationMembership = () => {
  const { getValues, setValue } = useFormContext<CreateMemberInput>();

  const [checked, setChecked] = useState<SubscriptionFrequency>("YEARLY");
  const [uploadDocumentText, setUploadDocumentText] = useState("Upload");
  const [errorMessage, setErrorMessage] = useState("");

  const { language } = useLanguage();

  const formTranslations =
    language === "En"
      ? englishTranslations.applyPage.applicationForm
      : frenchTranslations.applyPage.applicationForm;

  const handleUploadImage = async (
    files: FileList | null,
    key: "idPicturePath" | "foreignResidencyDocumentPath"
  ) => {
    if (!files || !files[0]) return;

    const path =
      key === "idPicturePath"
        ? "profile/id-cards"
        : "profile/foreign-residency-documents";

    try {
      const uploadKey = await uploadFile({
        file: files[0],
        isPublic: false,
        path,
      });
      setValue(key, uploadKey);
      setUploadDocumentText(formTranslations.uploadSuccessText);
    } catch (error) {
      console.error(error);
      setUploadDocumentText(formTranslations.uploadDocumentText);
      setErrorMessage(formTranslations.uploadErrorText);
    }
  };

  const handleSetChecked: MouseEventHandler<HTMLInputElement> = (e) => {
    setChecked(
      (e.target as EventTarget & { value: SubscriptionFrequency }).value
    );
  };

  // isUnder30 gets the value of the birthdate input, subtracts the year of it from the current year to check if the user is over 30
  const birthdate = new Date(getValues("birthDate"));
  birthdate.setFullYear(birthdate.getFullYear() + 30);
  const isUnder30 = birthdate > new Date();

  const internationalMember = getValues("placeOfResidency") != "FR";

  const monthlyPrice = isUnder30 || internationalMember ? 50 : 100;
  const yearlyPrice = isUnder30 || internationalMember ? 600 : 1200;

  type InstallmentRadioProps = {
    placeholder: string;
    label: string;
    value: SubscriptionFrequency;
  };

  useEffect(() => {
    setValue(
      "subscriptionType",
      isUnder30 || internationalMember ? "REDUCED_MEMBER" : "MEMBER"
    );
  }, [isUnder30, internationalMember, setValue]);

  useEffect(() => {
    const text =
      getValues("idPicturePath") || getValues("foreignResidencyDocumentPath")
        ? formTranslations.uploadSuccessText
        : formTranslations.uploadDocumentText;
    setUploadDocumentText(text);
  }, [getValues, formTranslations]);

  const InstallmentRadio = ({
    value,
    label,
    placeholder,
  }: InstallmentRadioProps) => {
    return (
      <ApplicationInput
        register="subscriptionFrequency"
        type="radio"
        placeholder={placeholder}
        label={label}
        value={value}
        checked={checked === value}
        onClick={handleSetChecked}
      />
    );
  };

  return (
    <>
      {isUnder30 && (
        <div>
          <p>Under 30</p>
          <p
            style={{ marginBottom: "0.25rem" }}
            dangerouslySetInnerHTML={{ __html: formTranslations.under30Text }}
          />

          <ApplicationInput
            register="idPicturePath"
            label="id picture"
            type="file"
            fileAccept={["image/png", "image/jpeg", "image/jpg"]}
            handleFileUpload={(e) => {
              handleUploadImage(e.target.files, "idPicturePath");
            }}
            style={{ background: "#fff" }}
            fileUploadText={uploadDocumentText}
          />
          <p style={{ marginTop: "0.5rem" }}>
            {formTranslations.imageConditions}
          </p>
          {!!errorMessage && <p>{errorMessage}</p>}
        </div>
      )}
      {/* {internationalMember && !isUnder30 && (
        <div>
          <p>International Resident</p>
          <p
            style={{ marginBottom: "0.25rem" }}
            dangerouslySetInnerHTML={{
              __html: formTranslations.foreignResidentText,
            }}
          />

          <ApplicationInput
            register="idPicturePath"
            label="id picture"
            type="file"
            fileAccept={[
              "image/png",
              "image/jpeg",
              "image/jpg",
              "application/pdf",
            ]}
            handleFileUpload={(e) => {
              handleUploadImage(e.target.files, "foreignResidencyDocumentPath");
            }}
            style={{ background: "#fff" }}
            fileUploadText={uploadDocumentText}
          />

          <p style={{ marginTop: "0.5rem" }}>
            {formTranslations.documentConditions}
          </p>
          {!!errorMessage && <p>{errorMessage}</p>}
        </div>
      )} */}
      <div>
        <p style={{ marginBottom: "0.25rem" }}>
          {formTranslations.installment}
        </p>
        <InstallmentRadio
          placeholder={`Mensuel: ${monthlyPrice} Euros`}
          label="monthly member subscription"
          value={"MONTHLY"}
        />
        <InstallmentRadio
          placeholder={`Annuel: ${yearlyPrice} Euros`}
          label="yearly member subscription"
          value={"YEARLY"}
        />
      </div>
      <div>
        <p>Sponsor</p>
        <p>{formTranslations.sponsorMessage}</p>
        <ApplicationInput
          register="sponsorCode"
          placeholder="Sponsor Code"
          label="Sponsor Code"
        />
      </div>
    </>
  );
};

export default ApplicationMembership;
